/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { accessAPI, Titulo, Boton } from "../../../shared/index.js";

import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Sidebar, tituloBase, TablaFiltrable } from "../../../shared";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import FormularioNuevoCliente from "./FormularioNuevoCliente.js";

const FILTROS = [
  { campo: "_etiquetas", label: "Categoría", labelPlural: "categorías", multiple: true }
];
const SEARCH_FIELDS = ["etiqueta"];

export default function Clientes() {
  const [loader, setLoader] = useState(true);
  const [clientes, setClientes] = useState();
  const [sidebarVisible, setSidebarVisible] = useState(false);

  const toast = useRef(null);
  const navigate = useNavigate();

  function cargarClientes() {
    setLoader(true);
    accessAPI(
      "GET",
      "admin/cliente",
      null,
      (response) => {
        setClientes(response);
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0].msg,
          sticky: true
        });
        setLoader(false);
      }
    );
  }

  // Cuando carga el componente, va a buscar la lista de clientes
  useEffect(() => {
    document.title = "Clientes";
    cargarClientes();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleClienteCreado(cliente) {
    setSidebarVisible(false);
    navigate(`/clientes/${cliente.id}`);
  }

  const certificadosTemplate = (rowData) => {
    if (rowData.certificados > 0) {
      return (
        <div css={{ color: "var(--verdeAcento)", fontWeight: "600" }}>
          {rowData.certificados}
        </div>
      );
    }
    return null;
  };

  const procesosTemplate = (rowData) => {
    if (rowData.procesos > 0) {
      return (
        <div css={{ color: "var(--grisTextos)", fontWeight: "600" }}>
          {rowData.procesos}
        </div>
      );
    }
    return null;
  };

  // _etiquetas: el array crudo de etiquetas de acciones, para el filtro
  // múltiple (OR entre ellas) de TablaFiltrable.
  const datos = (clientes || []).map((c) => ({
    ...c,
    _etiquetas: (c.clasificaciones || []).map((cl) => cl.etiqueta).filter(Boolean)
  }));

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Titulo
        botones={
          <Boton callback={() => setSidebarVisible(true)}>Nuevo cliente</Boton>
        }
      >
        Clientes
      </Titulo>

      <Sidebar
        visible={sidebarVisible}
        position="right"
        onHide={() => setSidebarVisible(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nuevo cliente</Titulo>
        <FormularioNuevoCliente onClienteCreado={handleClienteCreado} />
      </Sidebar>

      {loader && (
        <Card>
          <div
            css={{
              marginBottom: "1em",
              display: "flex",
              justifyContent: "flex-end"
            }}
          >
            <Skeleton width="33%" height="2.5rem" />
          </div>
          <div
            css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <Skeleton width="100%" height="3rem" />
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} width="100%" height="3.5rem" />
            ))}
          </div>
        </Card>
      )}

      {!loader && clientes && (
        <Card>
          <TablaFiltrable
            value={datos}
            filtros={FILTROS}
            searchFields={SEARCH_FIELDS}
            busquedaAlFinal
            anchoBusqueda="33%"
            nombreArchivo="clientes"
            stripedRows
            size="small"
            emptyMessage="No hay clientes registrados"
            rows={10}
            onRowClick={(e) => {
              const url = `/clientes/${e.data.value}/procesos`;
              const orig = e.originalEvent;
              // Command/Ctrl/middle-click abre en pestaña nueva
              if (
                orig?.metaKey ||
                orig?.ctrlKey ||
                orig?.button === 1
              ) {
                window.open(url, "_blank");
                return;
              }
              navigate(url);
            }}
            rowClassName={() => "cursor-pointer"}
            css={{
              "@media (max-width: 768px)": {
                "& .ocultar-en-mobile": {
                  display: "none !important"
                }
              }
            }}
          >
            <Column
              field="etiqueta"
              header="Nombre"
              body={(rowData) => (
                <span
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    // El alto de fila lo daba el botón del menú de acciones
                    // (eliminado); se mantiene igual que la tabla de colaboradores
                    minHeight: "3rem"
                  }}
                >
                  {rowData.etiqueta}
                  {rowData.pendientes > 0 && (
                    <span
                      css={{
                        backgroundColor: "var(--rojo)",
                        color: "white",
                        borderRadius: "50%",
                        minWidth: "22px",
                        height: "22px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.75rem",
                        fontWeight: "700",
                        lineHeight: 1
                      }}
                    >
                      {rowData.pendientes}
                    </span>
                  )}
                  {rowData.pagosPendientes > 0 && (
                    <Tag
                      value="Pagos pendientes"
                      css={{
                        fontSize: "0.7rem",
                        backgroundColor: "var(--rojo)",
                        color: "#ffffff"
                      }}
                    />
                  )}
                  {/* Los rubros salen de los procesos activos del cliente y
                      los define cada organismo en sus acciones para clientes.
                      Solo se muestran las acciones con etiqueta configurada. */}
                  {(rowData.clasificaciones || [])
                    .filter((c) => c.etiqueta)
                    .map((c) => (
                      <Tag
                        key={c.id}
                        className="ocultar-en-mobile"
                        value={c.etiqueta}
                        css={{ fontSize: "0.7rem", backgroundColor: "var(--principal)", color: "#ffffff" }}
                      />
                    ))}
                </span>
              )}
            />
            <Column
              field="procesos"
              header="Procesos"
              body={procesosTemplate}
              style={{ width: "100px", textAlign: "center" }}
              headerClassName="ocultar-en-mobile"
              bodyClassName="ocultar-en-mobile"
            />
            <Column
              field="certificados"
              header="Certificados"
              body={certificadosTemplate}
              style={{ width: "120px", textAlign: "center" }}
              headerClassName="ocultar-en-mobile"
              bodyClassName="ocultar-en-mobile"
            />
          </TablaFiltrable>
        </Card>
      )}
    </>
  );
}
